import Mock from 'mockjs';

// 演示模式开关：MOCK_MODE 由 vue.config.js 从 .env 读取后注入到客户端代码
// .env 中设置 MOCK_MODE=true 开启演示模式（无需后端即可使用全部前端功能）
export const IS_MOCK_MODE = process.env.MOCK_MODE === 'true';

// 初始化Mock
export function setupMock() {
    Mock.setup({
        timeout: '200-600' // 模拟网络延迟
    });
    // 常驻Mock：前端一直依赖的模拟接口，无论是否开启演示模式都注册
    registerPersistentMocks();
    // 演示模式Mock：原本由后端返回的数据接口，仅在演示模式下用 Mock 替代
    if (IS_MOCK_MODE) {
        registerDemoModeMocks();
    }
}

// 常驻Mock接口（始终启用，与演示模式开关无关）
function registerPersistentMocks() {
    // 添加每周睡眠分布数据接口
    Mock.mock(/\/api\/sleep\/weekly-distribution(\?.*)?/, 'get', (options) => {
        const params = new URLSearchParams(options.url.split('?')[1] || '')
        const patientId = params.get('patientId') || '1'
        return generateWeeklySleepData(patientId)
    })

    // 注册接口
    Mock.mock(/\/api\/register/, 'post', (options) => {
        const { username, password, name, role } = JSON.parse(options.body);
        if (!password || password.length < 6) {
            return { code: 401, msg: "password invalid" };
        }
        if (username === "admin") {
            return { code: 402, msg: "username already exists" };
        }
        return {
            code: 200,
            msg: "success",
            role,
            name,
            token: Mock.Random.guid()
        };
    });

    // 修改Mock接口
    Mock.mock(/\/api\/patients\/(\d+)/, 'get', (options) => {
        const id = options.url.match(/\/api\/patients\/(\d+)/)[1]
        const patient = generatePatient(id)
        return patient
            ? { code: 200, data: patient }
            : { code: 404, message: '患者不存在' }
    })

    // 上传文件接口
    Mock.mock(/\/api\/upload/, 'post', () => {
        return {
            code: 200,
            msg: "success",
            data: { record_id: 1 }
        };
    });

    // ==================== 睡眠改善方案相关Mock接口 ====================

    // 提交改善方案接口
    Mock.mock(/\/api\/treatment-plans/, 'post', (options) => {
        const planData = JSON.parse(options.body);
        const now = new Date();

        const newPlan = {
            id: String(Mock.Random.integer(1000, 9999)),
            patient_id: String(planData.patient_id),
            patient_name: planData.patient_name || Mock.Random.cname(),
            sleep_advice: planData.sleep_advice || '',
            depression_advice: planData.depression_advice || '',
            follow_up_plan: planData.follow_up_plan || '',
            duration: planData.duration || 30,
            status: planData.status || 'ongoing',
            created_at: now.toISOString().replace('T', ' ').substring(0, 19),
            created_by: 'doctor',
            currentDay: 0,
            summary: `睡眠改善方案 #${Mock.Random.integer(1000, 9999)}`
        };

        // 添加到存储
        treatmentPlansStorage.push(newPlan);

        return {
            code: 200,
            msg: '提交成功',
            data: newPlan
        };
    });

    // 获取患者的所有改善方案
    Mock.mock(/\/api\/treatment-plans\/(\d+)/, 'get', (options) => {
        const patientId = options.url.match(/\/api\/treatment-plans\/(\d+)/)[1];

        // 如果存储为空，生成一些示例数据
        if (treatmentPlansStorage.length === 0) {
            treatmentPlansStorage = [
                generateTreatmentPlan(patientId, 1001),
                generateTreatmentPlan(patientId, 1002),
                generateTreatmentPlan(patientId, 1003)
            ];
        }

        // 过滤出该患者的方案
        const plans = treatmentPlansStorage.filter(plan => plan.patient_id === String(patientId));

        return {
            code: 200,
            msg: '获取成功',
            data: plans
        };
    });

    // 根据ID获取单个改善方案
    Mock.mock(/\/api\/treatment-plan\/(\d+)/, 'get', (options) => {
        const planId = options.url.match(/\/api\/treatment-plan\/(\d+)/)[1];

        // 如果存储为空，生成示例数据
        if (treatmentPlansStorage.length === 0) {
            const patientId = Mock.Random.integer(10001, 10003);
            treatmentPlansStorage = [generateTreatmentPlan(patientId, planId)];
        }

        let plan = treatmentPlansStorage.find(p => p.id === String(planId));

        // 如果找不到，生成一个新的
        if (!plan) {
            const patientId = Mock.Random.integer(10001, 10003);
            plan = generateTreatmentPlan(patientId, planId);
            treatmentPlansStorage.push(plan);
        }

        return {
            code: 200,
            msg: '获取成功',
            data: plan
        };
    });

    // 删除改善方案
    Mock.mock(/\/api\/treatment-plan\/(\d+)/, 'delete', (options) => {
        const planId = options.url.match(/\/api\/treatment-plan\/(\d+)/)[1];
        const index = treatmentPlansStorage.findIndex(p => p.id === String(planId));

        if (index > -1) {
            treatmentPlansStorage.splice(index, 1);
            return {
                code: 200,
                msg: '删除成功',
                data: null
            };
        } else {
            return {
                code: 404,
                msg: '方案不存在',
                data: null
            };
        }
    });
}

// 演示模式Mock接口（原本由后端返回的数据，仅在演示模式下用 Mock 替代）
function registerDemoModeMocks() {
    // 模拟用户登录接口：演示模式下根据用户名/角色返回固定演示账号
    // 医生演示账号：doctor；病人演示账号：patient（对应患者数据集中的 10001 王立群）
    Mock.mock(/\/api\/login/, 'post', (options) => {
        const { username, role } = JSON.parse(options.body);
        const isDoctor = role === 'doctor' || username === 'doctor' || username === 'admin';
        const isPatient = role === 'patient' || username === 'patient';
        if (isDoctor) {
            return {
                code: 200,
                msg: "success",
                token: Mock.Random.guid(),
                id: 1,
                name: "王医生",
                role: "doctor"
            };
        }
        if (isPatient) {
            return {
                code: 200,
                msg: "success",
                token: Mock.Random.guid(),
                id: 10001,
                name: "王立群",
                role: "patient"
            };
        }
        return {
            code: 401,
            msg: '用户名或密码错误'
        };
    });

    // 模拟用户登出接口
    Mock.mock(/\/api\/logout/, 'post', () => {
        return {
            code: 200,
            msg: '登出成功'
        };
    });

    // 医生获取病人列表接口：直接返回患者数据集，保证与详情接口数据一致
    Mock.mock(/\/api\/fetchPatients/, 'get', () => {
        return {
            code: 200,
            msg: "success",
            data: patients
        };
    });

    // 医生获取病人详情接口：基于患者数据集返回固定数据，避免每次刷新变化
    Mock.mock(/\/api\/fetchPatientDetail\/\d+/, 'get', (options) => {
        const id = options.url.match(/\/api\/fetchPatientDetail\/(\d+)/)[1];
        const patient = patients.find((item) => item.id === Number(id));
        if (!patient) {
            return { code: 404, msg: '患者不存在' };
        }
        return {
            code: 200,
            msg: "success",
            data: {
                // 患者数据集中未包含的基础信息，使用固定演示数据补齐
                height: 172,
                weight: 65,
                education: "本科",
                address: "北京市海淀区西二旗",
                ...patient,
                summary: {
                    monthly_asleep_duration: 3600,
                    monthly_deep_duration: 3000,
                    target_duration: 4000,
                    dot: ["星期一", "星期二", "星期三", "星期四", "星期五", "星期六", "星期日"].map(week => ({ week, size: Array(24).fill(360) }))
                }
            }
        };
    });

    // 医生获取病历接口
    Mock.mock(/\/api\/fetchRecords\/\d+/, 'get', (options) => {
        const id = options.url.match(/\/api\/fetchRecords\/(\d+)/)[1];
        const total_duration = Mock.Random.integer(14400, 43200); // 先定义变量
        const stageData = generateStage(total_duration); // 传参
        return {
            code: 200,
            msg: "success",
            data: {
                id: Number(id),
                name: Mock.Random.cname(),
                createtime: "2021-01-01 12:00:00",
                sleep: {
                    total_duration: total_duration,
                    ...stageData,
                    stages: ["w", "n1", "n2", "n3", "w", "rem", "n2", "n3"],
                    advice: "建议进行锻炼，保持良好的睡眠习惯。",
                    eeg_signal: Array.from({ length: 2880 }, () => Math.random() * 2 - 1),
                    eog_signal: Array.from({ length: 2880 }, () => Math.random() * 2 - 1),
                    sampling_rate_hz: 100,
                    eeg_preview_url: `/sleep_results/${id}/2021-01-01-12-00-00/eeg_sample.jpg`,
                    relationship_img_url: `/sleep_results/${id}/2021-01-01-12-00-00/relationships.jpg`,
                    interpretation_img_url: `/sleep_results/${id}/2021-01-01-12-00-00/interpretation.jpg`
                },
                depression_ratio: {
                    none: 0,
                    low: 20,
                    medium: 30,
                    high: 50
                },
                features7: [
                    { name: "入睡潜伏期", value: Mock.Random.integer(20, 100) },
                    { name: "觉醒次数", value: Mock.Random.integer(20, 100) },
                    { name: "睡眠", value: Mock.Random.integer(20, 100) },
                    { name: "REM比例", value: Mock.Random.integer(20, 100) },
                    { name: "慢波睡眠比例", value: Mock.Random.integer(20, 100) },
                    { name: "浅睡眠比例", value: Mock.Random.integer(20, 100) },
                    { name: "REM潜伏期", value: Mock.Random.integer(20, 100) }
                ],
                depression_img_url: `/sleep_results/${id}/2021-01-01-12-00-00/depression.jpg`
            }
        };
    });
}

/*-----------------------------以下为数据生成函数----------------------------*/
// eslint-disable-next-line no-unused-vars
function generateStage(total_duration) {
    // 随机生成 n1_ratio, n2_ratio, n3_ratio, rem_ratio
    const n1_ratio = Math.random() * (30 - 25) + 25; // 25-30
    const n2_ratio = Math.random() * (25 - 20) + 20; // 20-25
    const n3_ratio = Math.random() * (20 - 15) + 15; // 15-20
    const rem_ratio = Math.random() * (8 - 5) + 5;   // 5-8

    // 计算 w_ratio
    const w_ratio = 100 - n1_ratio - n2_ratio - n3_ratio - rem_ratio;

    // 确保 w_ratio 不为负数
    if (w_ratio < 0) {
        throw new Error("w_ratio cannot be negative. Please adjust the ranges.");
    }

    // 计算各个阶段的 total_duration
    const n1_duration = Math.round(total_duration * (n1_ratio / 100));
    const n2_duration = Math.round(total_duration * (n2_ratio / 100));
    const n3_duration = Math.round(total_duration * (n3_ratio / 100));
    const w_duration = Math.round(total_duration * (w_ratio / 100));
    const rem_duration = Math.round(total_duration * (rem_ratio / 100));

    // 返回结果对象
    return {
        total_duration: total_duration,
        asleep_duration: total_duration - w_duration,
        n1_duration: n1_duration,
        n2_duration: n2_duration,
        n3_duration: n3_duration,
        w_duration: w_duration,
        rem_duration: rem_duration,
        n1_ratio: n1_ratio,
        n2_ratio: n2_ratio,
        n3_ratio: n3_ratio,
        w_ratio: w_ratio,
        rem_ratio: rem_ratio
    };
}

// 使用统一的数据生成函数

const patients = [
    {
        id: 10001,
        name: "王立群",
        age: 45,
        gender: "男",
        phone: "13812345678",
        JSON: [
            {
                time: Mock.Random.date().replace(/^(....)/, () => {
                    return Mock.Random.pick([2020, 2021, 2022, 2023, 2024, 2025]);
                }) + ` ${Mock.Random.time()}`,
                type: "初诊",
                text: "首次就诊，主诉持续性头痛和失眠",
            },
            {
                time: "2025-05-01",
                type: "复诊",
                text: "复诊，头痛症状有所缓解，但失眠依旧",
            },
        ],
        records: [
            {
                id: 1,
                createtime: "2025-04-01 20:00:00",
                total_duration: 3600,
                score: 70,
                asleep_duration: 3000,
                n1_duration: 600,
                n2_duration: 1200,
                n3_duration: 1800,
                w_duration: 600,
                rem_duration: 600,
                n1_ratio: 20,
                n2_ratio: 40,
                n3_ratio: 40,
                w_ratio: 10,
                rem_ratio: 10,
                sleepStages: {
                    awake: 10,
                    light: 30,
                    deep: 20,
                    rem: 15,
                },

                reportUrl: "/report/1001",
            },

        ],
    },
    {
        id: 10002,
        name: "李晓红",
        age: 38,
        gender: "女",
        phone: "13912345678",
        JSON: [
            {
                time: "2025-03-01",
                type: "急诊",
                text: "因突发胸痛急诊入院",
            },
            {
                time: "2025-04-15",
                type: "复诊",
                text: "复诊，胸痛症状减轻",
            },
        ],
        records: [
            {
                id: 2,
                createtime: "2025-03-01 20:00:00",
                total_duration: 100,
                score: 80,
                sleepStages: {
                    awake: 5,
                    light: 40,
                    deep: 25,
                    rem: 10,
                },

                reportUrl: "/report/2001",
            },
            {
                id: 3,
                createtime: "2025-04-15 20:00:00",
                total_duration: 130,
                score: 85,
                sleepStages: {
                    awake: 8,
                    light: 38,
                    deep: 22,
                    rem: 12,
                },

                reportUrl: "/report/2002",
            },
        ],
    },
    {
        id: 10003,
        name: "张伟",
        age: 52,
        gender: "男",
        phone: "13712345678",
        JSON: [
            {
                time: "2025-01-01",
                type: "初诊",
                text: "首次就诊，主诉持续性头痛和失眠",
            },
            {
                time: "2025-02-01",
                type: "复诊",
                text: "复诊，头痛症状有所缓解，但失眠依旧",
            },
            {
                time: "2025-03-01",
                type: "急诊",
                text: "因突发胸痛急诊入院",
            },
        ],
        records: [
            {
                id: 4,
                createtime: "2025-01-01 20:00:00",
                total_duration: 110,
                score: 75,
                sleepStages: {
                    awake: 15,
                    light: 32,
                    deep: 18,
                    rem: 10,
                },

                reportUrl: "/report/3001",
            },
            {
                id: 5,
                createtime: "2025-02-01 20:00:00",
                total_duration: 140,
                score: 70,
                sleepStages: {
                    awake: 10,
                    light: 30,
                    deep: 20,
                    rem: 15,
                },

                reportUrl: "/report/3002",
            },
            {
                id: 6,
                createtime: "2025-03-01 20:00:00",
                total_duration: 160,
                score: 60,
                sleepStages: {
                    awake: 20,
                    light: 25,
                    deep: 15,
                    rem: 10,
                },

                reportUrl: "/report/3003",
            },
        ],
    },
];
export const generatePatient = (id) => {
    // 将传入的 id 转换为整数
    const parsedId = parseInt(id, 10);

    // 检查转换后的 id 是否为有效的整数
    if (isNaN(parsedId)) {
        throw new TypeError("传入的 id 不是有效的数字");
    }

    const patient = patients.find((item) => item.id === parsedId);
    if (patient) {
        return patient;
    } else {
        throw new Error("未找到对应的病人");
    }
};

export const generateWeeklySleepData = () => {
    const days = [
        "星期一",
        "星期二",
        "星期三",
        "星期四",
        "星期五",
        "星期六",
        "星期日",
    ];
    return Mock.mock({
        code: 200,
        data: {
            days,
            timeData: days.map(
                () =>
                    Mock.mock({
                        "data|5-10": [
                            {
                                "time|0-100": 80, //x轴坐标
                                "value|0-60": 10, //深睡分钟数
                                size: function () {
                                    return this.value;
                                },
                                intensity: function () {
                                    // return this.value / 50;
                                    return 0.8;
                                },
                            },
                        ],
                    }).data
            ),
        },
    });
};

// 存储改善方案数据（内存存储，刷新后重置）
let treatmentPlansStorage = [];

// 生成改善方案Mock数据
const generateTreatmentPlan = (patientId, planId = null) => {
    const id = planId || Mock.Random.integer(1000, 9999);
    const status = Mock.Random.pick(['draft', 'ongoing', 'completed']);

    return {
        id: String(id),
        patient_id: String(patientId),
        patient_name: Mock.Random.cname(),
        sleep_advice: [
            '建立规律的睡眠时间表，每天固定时间上床和起床，即使在周末也要保持一致',
            '睡前1小时避免使用电子设备，保持卧室环境安静、黑暗、凉爽',
            '如果躺在床上20分钟内无法入睡，应起床进行轻松活动，直到感到困倦再回到床上',
            '避免在睡前3小时内进食大量食物，特别是辛辣、油腻食物',
            '保持卧室温度在18-22°C之间，湿度控制在50-60%'
        ].join('\n'),
        depression_advice: [
            '学习正念冥想和深呼吸技巧，每天练习10-15分钟',
            '每周进行3-5次中等强度有氧运动，每次30分钟以上',
            '保持均衡饮食，多摄入新鲜蔬果和富含欧米伽-3脂肪酸的食物',
            '培养兴趣爱好，参与自己喜欢和有意义的活动',
            '保持规律的社交活动，主动与家人朋友交流'
        ].join('\n'),
        follow_up_plan: '建议2周后复查睡眠数据，评估改善效果。如症状持续或加重，请及时复诊。',
        duration: Mock.Random.pick([14, 21, 30, 60]),
        status: status,
        created_at: Mock.Random.datetime('yyyy-MM-dd HH:mm:ss'),
        created_by: 'doctor',
        currentDay: status === 'completed' ? null : Mock.Random.integer(1, 25),
        summary: `睡眠改善方案 #${id}`
    };
};
